import { CoherencePanel } from "@coherence-design-system/controls";
import {
  Checkbox,
  DatePicker,
  DefaultButton,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Separator,
  Stack,
  TextField,
  addDays,
  defaultDatePickerStrings,
} from "@fluentui/react";
import React, { useContext, useState } from "react";
import { useDateFormatter } from "../../../../Utils/DateFormatter";
import {
  OverridePanelPaddingStyle,
  SeparatorStyles,
  buttonStyleWithWidth100,
  stackTokensWithGap20,
  textFieldStyles300,
} from "../../QuotationStyle";
import {
  ISignQuotationComponentProps,
  ISignQuotationFormData,
  ISignQuotationErrorState,
} from "./SignQuotationComponent.types";
import {
  InitialSignQuotationFormData,
  InitialSignQuotationErrorState,
  PaymentOptions,
  InitialStateOfResponseMessage,
  QuotationStatus,
} from "../../Common/QuotationData";
import {
  datePickerStyles,
  textFieldWidth300GrayStyles,
} from "./SignQuotationComponentStyle";
import { QuotationService } from "../../../../services/Quotations/QuotationService";
import { appInsights } from "../../../../services/Logging/AppInsights";
import { IResponseMessage, QuotationInfoType } from "../../Quotation.types";
import { QuotationContext } from "../../QuotationContext";
import { poNumberRegex, CUSTOMER_PURCHASE_ORDER_VALIDATION } from "../../../../Utils/Common";
var isEqual = require("lodash.isequal");

export const SignQuotationComponent: React.FunctionComponent<
  ISignQuotationComponentProps
> = ({
  isPanelOpen,
  dismissPanel,
  quotationId,
  quotationNumber,
  partitionKey,
  customerName,
  createdAt,
  paymentType,
  customerPurchaseOrderNumber,
  setHasSignedSuccessfully,
  setUpdatedStatus,
  setQuotationErrorMessage,
  standaloneItems,
  bundleItems,
}) => {
  const { quotationInfo, setQuotationInfo } = useContext(
    QuotationContext
  ) as QuotationInfoType;

  // --- Date formatting for country ---
  const getCountryName = () => {
    const address = quotationInfo.customer?.selectedShipToAddress;
    if (!address) return undefined;
    return address.country;
  };
  const { formatDate } = useDateFormatter(getCountryName());
  // Wrapper to match DatePicker's expected signature
  const formatDateForPicker = (date?: Date | undefined) => {
    if (!date) return "";
    return formatDate(date);
  };
  const [formData, setFormData] = useState<ISignQuotationFormData>({
    ...InitialSignQuotationFormData,
    quotationNumber,
    customerName,
    customerPurchaseOrderNumber,
    quotationSignatureDate: new Date().toUTCString(),
  });
  const [errorState, setErrorState] = useState<ISignQuotationErrorState>(
    InitialSignQuotationErrorState
  );
  const [quotationSuccessMessage, setQuotationSuccessMessage] =
    useState<IResponseMessage>(InitialStateOfResponseMessage);
  const [quotationFailureMessage, setQuotationFailureMessage] =
    useState<IResponseMessage>(InitialStateOfResponseMessage);
  const onClickSubmit = () => {
    if (validateFields()) {
      const signQuotationPayload = {
        quoteId: quotationId,
        partitionKey,
        signedDate: new Date(formData.quotationSignatureDate),
        isValidated: formData.validatedQuotationAndCustomerPO,
        PONumber: formData.customerPurchaseOrderNumber,
      };

      QuotationService.signQuotation(signQuotationPayload).then((res) => {
        if (res !== undefined && res.data.data) {
          appInsights.trackEvent({
            name: "In SignQuotationComponent.tsx, quotation has been confirmed/signed successfully.",
            properties: { quotationId },
          });
          setQuotationInfo({
            ...quotationInfo,
            customerPurchaseOrder: formData.customerPurchaseOrderNumber,
          });
          setQuotationSuccessMessage({
            message: `Quotation has been signed successfully`,
            showMessage: true,
          });
          setTimeout(() => {
            setQuotationSuccessMessage(InitialStateOfResponseMessage);
            dismissPanel();
            setHasSignedSuccessfully && setHasSignedSuccessfully(true);
            setUpdatedStatus && setUpdatedStatus(QuotationStatus.Confirmed);
          }, 3000);
        } else if (
          res !== undefined &&
          res.data.code !== 200 &&
          res.data.message
        ) {
          setQuotationFailureMessage({
            message: res.data.message.replace(",", "\n"),
            showMessage: true,
          });
          setTimeout(() => {
            setQuotationFailureMessage(InitialStateOfResponseMessage);
          }, 10000);
        }
      });
    }
  };
  const validateFields = () => {
    quotationFailureMessage.showMessage &&
      setQuotationFailureMessage(InitialStateOfResponseMessage);
    quotationSuccessMessage.showMessage &&
      setQuotationSuccessMessage(InitialStateOfResponseMessage);
    const updatedErrorState = {
      ...errorState,
    };
    if (!quotationId || !formData.quotationNumber) {
      updatedErrorState.quotationNumber = true;
    }
    if (!formData.customerName) {
      updatedErrorState.customerName = true;
    }
    if (!formData.quotationSignatureDate) {
      updatedErrorState.quotationSignatureDate = true;
    }
    if (!formData.validatedQuotationAndCustomerPO) {
      updatedErrorState.validatedQuotationAndCustomerPO = true;
      setQuotationFailureMessage({
        message: `Please validate Quote and Customer PO details`,
        showMessage: true,
      });
      setTimeout(() => {
        setQuotationFailureMessage(InitialStateOfResponseMessage);
      }, 10000);
    }
    if (
      paymentType === PaymentOptions[1].key &&
      !formData.customerPurchaseOrderNumber
    ) {
      updatedErrorState.customerPurchaseOrderNumber = true;
      setQuotationFailureMessage({
        message: `PO number is mandatory which is missing. Please provide customer PO number to sign the quote.`,
        showMessage: true,
      });
      setTimeout(() => {
        setQuotationFailureMessage(InitialStateOfResponseMessage);
      }, 10000);
    } else if (
      formData.customerPurchaseOrderNumber && 
      formData.customerPurchaseOrderNumber.length > 0 && 
      !poNumberRegex?.test(formData.customerPurchaseOrderNumber)
    ) {
      updatedErrorState.customerPurchaseOrderNumber = true;
      setQuotationFailureMessage({
        message: CUSTOMER_PURCHASE_ORDER_VALIDATION+". Please correct the customer PO number and try again.",
        showMessage: true,
      });
      setTimeout(() => {
        setQuotationFailureMessage(InitialStateOfResponseMessage);
      }, 10000);
    }
    if (standaloneItems && bundleItems) {
      const isInvalidStandalone = quotationInfo.standaloneItems.some(
        (i) => i.discountedPercentage > 100
      );
      const inInvalidBundle = quotationInfo.bundleItems.some(
        (j) => j.discountedPercentage > 100
      );
      if (isInvalidStandalone || inInvalidBundle) {
        updatedErrorState.lineItemDiscounts = true;
        setQuotationFailureMessage({
          message: `Unable to sign a quote having line item discount greater than 100%. Please correct the discount and try again.`,
          showMessage: true,
        });
        setQuotationErrorMessage({
          message: `Unable to sign a quote having line item discount greater than 100%. Please correct the discount and try again.`,
          showMessage: true,
        });
        setTimeout(() => {
          setQuotationFailureMessage(InitialStateOfResponseMessage);
        }, 10000);
      }
    }
    setErrorState(updatedErrorState);
    return isEqual(updatedErrorState, InitialSignQuotationErrorState);
  };
  const resetFormData = () => {
    setFormData(InitialSignQuotationFormData);
    dismissPanel();
  };
  const onRenderFooter = () => {
    return (
      <Stack
        verticalFill
        verticalAlign="center"
        style={{ width: "100%", padding: 0 }}
      >
        <Separator styles={SeparatorStyles} />
        <Stack horizontal horizontalAlign="start">
          <PrimaryButton
            text="Submit"
            type="button"
            onClick={onClickSubmit}
            style={buttonStyleWithWidth100}
          />
          <DefaultButton
            text="Cancel"
            type="button"
            onClick={resetFormData}
            style={buttonStyleWithWidth100}
          />
        </Stack>
      </Stack>
    );
  };

  return (
    <CoherencePanel
      panelSize="450px"
      headerText="Confirm Customer Signature"
      titleText="Confirm Customer Signature"
      isOpen={isPanelOpen}
      onDismiss={resetFormData}
      onAbort={resetFormData}
      // You MUST provide this prop! Otherwise screen readers will just say "button" with no label.
      closeButtonAriaLabel="Close"
      onRenderFooterContent={onRenderFooter}
      onRenderFooter={onRenderFooter}
      isFooterAtBottom={true}
      styles={OverridePanelPaddingStyle}
    >
      {quotationSuccessMessage.showMessage && (
        <MessageBar
          messageBarType={MessageBarType.success}
          onDismiss={() =>
            setQuotationSuccessMessage(InitialStateOfResponseMessage)
          }
        >
          {quotationSuccessMessage.message}
        </MessageBar>
      )}
      {quotationFailureMessage.showMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          onDismiss={() =>
            setQuotationFailureMessage(InitialStateOfResponseMessage)
          }
        >
          {quotationFailureMessage.message}
        </MessageBar>
      )}
      <form>
        <Stack verticalFill verticalAlign="start">
          <TextField
            label="Quotation ID"
            name="quotationId"
            errorMessage={
              errorState.quotationNumber ? "Quotation Number is required" : ""
            }
            value={formData?.quotationNumber}
            styles={textFieldWidth300GrayStyles}
            readOnly
          />
          <TextField
            label="Customer Name"
            name="customerName"
            value={formData?.customerName}
            styles={textFieldWidth300GrayStyles}
            errorMessage={
              errorState.customerName ? "Customer Name is required" : ""
            }
            readOnly
          />
          <DatePicker
            styles={datePickerStyles}
            label="Date Customer Signed Quote"
            minDate={new Date(createdAt)}
            maxDate={new Date()}
            value={
              formData.quotationSignatureDate
                ? new Date(formData.quotationSignatureDate)
                : new Date()
            }
            isRequired
            onSelectDate={(date: Date | null | undefined) => {
              if (date) {
                // Set time to noon to avoid timezone shift
                const noonDate = new Date(date);
                noonDate.setHours(12, 0, 0, 0);
                setFormData({
                  ...formData,
                  quotationSignatureDate: noonDate.toUTCString(),
                });
              }
            }}
            formatDate={formatDateForPicker}
            strings={defaultDatePickerStrings}
          />
          <Stack tokens={stackTokensWithGap20}>
            <TextField
              label="Customer PO Number"
              name="customerPurchaseOrderNumber"
              value={formData.customerPurchaseOrderNumber}
              required={paymentType === PaymentOptions[1].key ? true : false}
              errorMessage={
                errorState.customerPurchaseOrderNumber && !formData.customerPurchaseOrderNumber
                  ? "PO number is required"
                  : errorState.customerPurchaseOrderNumber && formData.customerPurchaseOrderNumber
                  ? CUSTOMER_PURCHASE_ORDER_VALIDATION
                  : ""
              }
              onChange={(e, newValue) => {
                setFormData({
                  ...formData,
                  customerPurchaseOrderNumber: newValue ?? "",
                });
                if (newValue && newValue.length > 0 && !poNumberRegex?.test(newValue)) {
                  setErrorState({
                    ...errorState,
                    customerPurchaseOrderNumber: true,                   
                  });
                } else if (paymentType === PaymentOptions[1].key && !newValue) {
                  setErrorState({
                    ...errorState,
                    customerPurchaseOrderNumber: true,
                  });
                } else {
                  setErrorState({
                    ...errorState,
                    customerPurchaseOrderNumber: false,
                  });
                }
              }}
              styles={textFieldStyles300}
            />
            <Checkbox
              label="Validated Quote and Customer PO Number"
              checked={formData.validatedQuotationAndCustomerPO}
              required
              onChange={(e, newValue) => {
                if (errorState.validatedQuotationAndCustomerPO) {
                  setErrorState({
                    ...errorState,
                    validatedQuotationAndCustomerPO: false,
                  });
                }
                setFormData({
                  ...formData,
                  validatedQuotationAndCustomerPO: newValue ? newValue : false,
                });
              }}
            />
          </Stack>
        </Stack>
      </form>
    </CoherencePanel>
  );
};
